import { Alert, Box, Chip, CircularProgress, Drawer, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useHousehold } from '../api/hooks';
import { useAuth } from '../components/AuthContext';
import { RecTypeTag } from '../components/badges';
import { describeError } from '../components/Notify';
import { useUrlState } from '../urlState';
import { ActivitySection } from './ActivitySection';
import { ApplicantsSection } from './ApplicantsSection';
import { AssessmentSection } from './AssessmentSection';
import { DuplicatesSection } from './DuplicatesSection';
import { NarrativeSection } from './NarrativeSection';
import { RecommendationsSection } from './RecommendationsSection';
import { ReferralSection } from './ReferralSection';
import { WorkflowSection } from './WorkflowSection';

export function HouseholdDrawer() {
  const { can } = useAuth();
  const { params, set } = useUrlState();
  const id = params.get('h');
  const detail = useHousehold(id);
  const close = () => set({ h: null, r: null });
  const h = detail.data;
  const version = h ? `${h.id}-${h.updatedAt}-${h.override?.id ?? ''}-${h.nextFollowUpAt ?? ''}-${h.advancement.capacityBand}-${h.advancement.notes ?? ''}` : '';

  return (
    <Drawer
      anchor="right"
      open={!!id}
      onClose={close}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 640 }, maxWidth: '100%' }, 'aria-label': 'Household details' } }}
    >
      <Box sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: 'background.paper', px: 3, pt: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h2" component="h2" data-testid="drawer-title">
              {h?.familyLastName ?? (detail.isPending ? 'Loading…' : 'Household')}
            </Typography>
            {h && (
              <>
                <Typography variant="body2" color="text.secondary">
                  {h.applicants.map((a) => `${a.firstName}${a.gradeApplying ? ` (Gr ${a.gradeApplying})` : ''}`).join(', ') || 'No applicants'}
                  {h.parentNames ? ` · ${h.parentNames}` : ''}
                </Typography>
                <Stack direction="row" gap={0.5} mt={1} flexWrap="wrap">
                  <Chip size="small" label={h.origin === 'manual_prospect' ? 'Manual prospect' : 'Veracross'} color={h.origin === 'manual_prospect' ? 'secondary' : 'primary'} variant="outlined" />
                  {h.recommendationTypes.map((t) => (
                    <RecTypeTag key={t} type={t} />
                  ))}
                </Stack>
              </>
            )}
          </Box>
          <IconButton aria-label="Close details" onClick={close}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>
      <Box sx={{ px: 3, pb: 4 }}>
        {detail.isPending && id && (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
            <CircularProgress aria-label="Loading household" />
          </Box>
        )}
        {detail.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Could not load household: {describeError(detail.error)}
          </Alert>
        )}
        {h && (
          <Box key={version}>
            <DuplicatesSection h={h} />
            {can('referrals.review') && <ReferralSection h={h} />}
            <AssessmentSection h={h} />
            <NarrativeSection h={h} />
            <ApplicantsSection h={h} />
            <WorkflowSection h={h} />
            <RecommendationsSection h={h} />
            <ActivitySection h={h} />
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
